/**
 * Live image-styler viewer (no controls) — standalone frontend entry.
 *
 * Upgrades any `[data-image-styler]` element from a baked styled PNG to the
 * live, interactive WebGL styler — reproduced from the ORIGINAL image plus the
 * settings the editor saved. Currently used by the event-detail header
 * (template-parts/detail/event/header.php).
 *
 * This is its OWN top-level Vite entry (built as an ES module) and is enqueued
 * as `type="module"` only on single-event pages — see library/enqueue-scripts.php.
 * It deliberately reuses the exact engine that powers the editor/admin:
 *   - ImageStyler   (./image-styler/engine.js)
 *   - presetToState (./image-styler/generate.js)
 *
 * It is kept OUT of the main frontend bundle on purpose: dist/main.js is loaded
 * as a CLASSIC script, so pulling the (ESM, code-split) engine into its graph
 * would turn main.js into a module (import/export) and break the whole bundle in
 * production. Mirrors how image-styler-admin.js is wired.
 *
 * Motion: the noise/motion animates continuously while the banner is on-screen
 * (all devices). Mouse interactivity is intentionally DISABLED here — the cursor
 * adds no local distortion (the editor/home styler still has it). It pauses when
 * scrolled off-screen and falls back to the static image when WebGL is
 * unavailable or the visitor prefers reduced motion.
 */

import { ImageStyler } from './image-styler/engine.js'
import { presetToState } from './image-styler/generate.js'

const SELECTOR = '[data-image-styler]'

// Scroll effect (mirrors the home hero): as the page scrolls, the keyed colours
// spread out (takeover) and wash to WHITE — the "wegfressen" flood — kept FAST and
// front-loaded so the flood is already visible in the first few scrolled pixels and
// the fixed header clears early, keeping the incoming content readable. There is
// deliberately NO opacity fade: the banner washes to the page background colour
// (white, inverted to black in dark mode) rather than fading transparent.
// (0.65 of a viewport, i.e. twice the original 0.325 — the client asked for a
// longer scroll travel on the event headers; the home hero keeps its own timing.)
const FLOOD_END = 0.65 // takeover + white-wash complete within the first two thirds
// Once the flood has fully settled the banner is a static white field (washed to
// the background), so we stop rendering just past it to save GPU — nothing changes
// visually beyond this point.
const RENDER_STOP = 0.78

// Cap the live render rate. The noise animates slowly, so 30fps is visually
// indistinguishable from 60/120 while roughly halving the GPU/battery cost.
const MIN_FRAME_MS = 1000 / 30

// Cap the drawing-buffer resolution below the engine's default DPR for the live
// banner only (the admin editor + baked-PNG export keep full resolution). Weak
// devices (≤4 GB RAM or ≤4 cores) start at 1.0×; raise toward 2.0 for a sharper banner.
const LIVE_MAX_DPR =
  (navigator.deviceMemory && navigator.deviceMemory <= 4) ||
  (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) ? 1.0 : 1.5

const clamp01 = (x) => Math.max(0, Math.min(1, x))
// Ease-OUT, not smoothstep: the flood must be visibly under way from the first
// scrolled pixel. Smoothstep's ease-in left the banner looking frozen for the
// first ~50px before anything moved.
const easeOut = (e0, e1, x) => { const t = clamp01((x - e0) / (e1 - e0)); return 1 - (1 - t) * (1 - t) }
// Reuse scrollFx.js's published progress (inline custom prop → cheap, no recalc).
const readScrollProgress = () => {
  const v = parseFloat(document.body.style.getPropertyValue('--scroll-fx'))
  return Number.isFinite(v) ? v : clamp01(window.scrollY / (window.innerHeight || 1))
}

// The colour the flood washes into. NOT white: the flooded banner has to vanish into
// the page, and the page is #FCFCFC — a pure-white wash leaves a faint edge at the
// bottom of the fixed banner that shows up on bright displays. --styler-wash-color
// (settings.sass) carries the right value per colour scheme; see the comment there
// for why the dark-mode value is white rather than the black page background.
// getComputedStyle forces a style flush, so it is read once and memoised — dark mode
// can flip at runtime (the accessibility overlay), hence the body-class observer below.
const WASH_FALLBACK = '#ffffff'
const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i // the engine only parses hex — anything else → fallback
let washColor = null
const readWashColor = () => {
  if (washColor) return washColor
  const v = getComputedStyle(document.body).getPropertyValue('--styler-wash-color').trim()
  washColor = HEX.test(v) ? v : WASH_FALLBACK
  return washColor
}
// Dark mode is applied as a body class with no event to hook (accessibility.js only
// announces motion changes), so watch the class list and drop the memo when it moves.
new MutationObserver(() => { washColor = null })
  .observe(document.body, { attributes: true, attributeFilter: ['class'] })

// Static-fallback gate: reduced-motion OR reduced-data / Save-Data → keep the
// baked poster image (weak/metered sessions skip the live WebGL styler).
// Motion is read off the `.reduce-motion` body class (accessibility.js) rather
// than the raw `prefers-reduced-motion` query: the class already defaults to that
// query but lets the accessibility overlay's quiet-mode toggle override it, so
// turning quiet mode OFF restores the live styler even when the OS prefers reduced
// motion. Data / Save-Data stay independent — they gate the heavy WebGL download.
const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-data: reduce)').matches ||
  (navigator.connection && navigator.connection.saveData === true) ||
  document.body.classList.contains('reduce-motion') // effective quiet mode (src/js/accessibility.js)

// Band colours a preset authors as "the page background": pure white, or the
// light-mode background itself. On an OPAQUE render (Sarah's tool, the baked teaser)
// such a band is real paint laid onto the photo. This banner is the opposite — a
// TRANSPARENT layer over the page — so there the band only ever means "let the page
// show through", and a literal #ffffff lands 3/255 above the #FCFCFC page: a flat,
// slightly-too-bright rectangle that is invisible on most screens and obvious on a
// good one.
// So swap them for --styler-wash-color at render time — the page background for the
// CURRENT colour scheme. That is also why this is done live instead of baked into the
// preset: in dark mode the wrapper is inverted (50_pages.sass) and the correct
// PRE-filter value is #fff, not #FCFCFC. One baked colour cannot be right in both
// schemes; this can. Doing it here also means events whose saved settings_json still
// holds the older #ffffff render correctly without being re-rendered.
const BG_BAND_COLORS = new Set(['#fff', '#ffffff', '#fcfcfc'])
const BAND_KEYS = ['c0', 'c1', 'c2', 'c3', 'c4']
const bgBandOverrides = (state, wash) => {
  const out = {}
  for (const k of BAND_KEYS) {
    const v = state[k]
    if (typeof v === 'string' && BG_BAND_COLORS.has(v.toLowerCase())) out[k] = wash
  }
  return out
}

// The banner is held at opacity 0 (50_pages.sass) until it can show its FINAL look,
// then fades up fast. This hands out the class that does it — called both when the
// live canvas paints its first frame and on EVERY path that bails to the baked
// poster, which otherwise would stay invisible for good.
const reveal = (el) => el.classList.add('is-styler-ready')

// el -> { setVisible(bool) | null }. Lets the IntersectionObserver drive
// pause/resume even for visibility changes that land mid-initialisation.
const controllers = new WeakMap()

function onIntersect(entries) {
  for (const entry of entries) {
    const ctrl = controllers.get(entry.target)
    if (ctrl) {
      if (ctrl.setVisible) ctrl.setVisible(entry.isIntersecting)
      continue
    }
    if (!entry.isIntersecting) continue // wait until it first scrolls into view
    const placeholder = { setVisible: null }
    controllers.set(entry.target, placeholder)
    initStyler(entry.target, placeholder)
  }
}

async function initStyler(el, ctrl) {
  // 1. Settings + source — bail to the static poster if either is missing/bad.
  let settings
  try { settings = JSON.parse(el.dataset.stylerSettings || '') }
  catch { reveal(el); return }
  if (!settings || typeof settings !== 'object') { reveal(el); return }
  const src = el.dataset.stylerSrc
  if (!src) { reveal(el); return }
  if (prefersReducedMotion()) { reveal(el); return } // re-check: the a11y class may settle after page load

  // 2. WebGL engine — bail (leave poster) if unavailable.
  const canvas = document.createElement('canvas')
  canvas.className = 'event-detail-header-image-canvas'
  canvas.setAttribute('aria-hidden', 'true')
  el.appendChild(canvas)

  let engine
  try { engine = new ImageStyler(canvas) }
  catch { canvas.remove(); reveal(el); return } // WebGL unsupported → poster img stays as the fallback
  engine.maxDpr = LIVE_MAX_DPR

  // 3. Per-instance run state.
  const wrapper = el.closest('.event-detail-header-image-wrapper') || el
  // Mouse interactivity disabled on the event-detail header: park the cursor far
  // off-canvas so the shader's local distortion is always ~0 (only the base
  // ambient motion runs) — no pointermove listener feeds this. Not centred,
  // which would leave a static distortion blob in the middle of the banner.
  const mouse = { x: -1, y: -1 }
  let visible = true
  let running = false
  let started = false
  let revealed = false
  let rafId = 0
  let appliedWash = null // wash colour the background bands were last mapped to
  let t0 = 0
  let elapsed = 0 // animation clock in seconds; only advances while running
  let lastDraw = 0 // performance.now() of the last drawn frame (fps cap)

  // The noise/motion animates continuously while the banner is on-screen; the
  // cursor adds no distortion here (disabled). Once the flood has fully settled
  // (scrolled past RENDER_STOP) the banner is a static white field, so we freeze on
  // the last frame rather than keep redrawing an unchanging image every frame.
  const isCovered = () => readScrollProgress() >= RENDER_STOP
  const wantsRun = () => started && visible && !isCovered() && !engine._lost
  // GPU context lost → stop and restore the baked poster <img> fallback.
  const onLost = () => {
    stop()
    el.classList.remove('is-styler-live')
    canvas.remove()
  }

  const revealIfReady = () => {
    if (revealed || canvas.width <= 0 || canvas.height <= 0) return
    revealed = true
    el.classList.add('is-styler-live') // canvas in, poster out — both still behind opacity 0
    reveal(el)                         // ...then fade the finished banner up
  }

  // Scroll → spread the keyed colours out (takeover) and wash them into the page
  // background (the "wegfressen" flood). No inline opacity: canvas opacity is left
  // entirely to CSS (the poster→canvas reveal via is-styler-live), so the banner
  // washes to the background colour instead of fading transparent.
  const applyScroll = () => {
    const p = readScrollProgress()
    const flood = clamp01(easeOut(0, FLOOD_END, p))
    const wash = readWashColor()
    // Re-map the background bands whenever the wash moves. Not once at init: the
    // accessibility overlay can flip dark mode at runtime, and the inverted wrapper
    // then needs the OTHER pre-filter value. Guarded so this is a no-op per frame.
    if (wash !== appliedWash) {
      appliedWash = wash
      engine.setState(bgBandOverrides(baseState, wash))
    }
    engine.setState({ scrollTakeover: flood, whiteOut: flood, washColor: wash })
  }

  const tick = (now) => {
    if (engine._lost) { onLost(); return } // GPU context lost → restore poster
    rafId = requestAnimationFrame(tick)
    if (now - lastDraw < MIN_FRAME_MS) return // fps cap
    lastDraw = now
    applyScroll()
    engine.render(elapsed + (now - t0) / 1000, mouse)
    revealIfReady()
  }
  const start = () => {
    if (running) return
    running = true
    t0 = performance.now()
    rafId = requestAnimationFrame(tick)
  }
  const stop = () => {
    if (!running) return
    running = false
    cancelAnimationFrame(rafId)
    elapsed += (performance.now() - t0) / 1000 // freeze the clock where it stopped
  }
  const renderStatic = () => { // one frozen frame (idle / paused / after resize)
    applyScroll()
    engine.render(elapsed, mouse)
    revealIfReady()
  }
  const update = () => {
    if (wantsRun()) start()
    // Always draw one last frame before freezing. Skipping the draw past RENDER_STOP
    // (on the theory that the flood has already settled) assumes the last frame we
    // drew was the flooded one — and that is not guaranteed: a single fast scroll can
    // cross the whole flood window between two drawn frames (a trackpad fling, or the
    // "#tickets" anchor jump straight down the page), and the fps cap can drop the
    // frames in between. The loop then stops on an un-flooded frame and the banner
    // stays frozen at full strength over the content until you scroll back up.
    else if (running) { stop(); renderStatic() }
  }

  ctrl.setVisible = (v) => { visible = v; update() }

  // The IntersectionObserver can't pause us: the header wrapper is position:fixed,
  // so it always reports as intersecting. Drive pause/resume off scroll instead —
  // once the canvas fades out we stop the loop; scrolling back toward the top
  // resumes it. rAF-batched so it costs ~nothing per scroll event.
  let scrollRaf = 0
  const onScroll = () => {
    if (scrollRaf) return
    scrollRaf = requestAnimationFrame(() => { scrollRaf = 0; update() })
  }
  window.addEventListener('scroll', onScroll, { passive: true })

  // 4. Cover-fit override: the shader fits CONTAIN, but the header is cover.
  // Sizing the canvas CSS box to cover (at the image AR) makes uvContain a no-op
  // so the image fills the box; the wrapper's overflow:hidden crops it. Only this
  // instance is patched — the shared editor/admin engine is untouched.
  engine.fitCanvasToImage = () => {
    const pw = el.clientWidth
    const ph = el.clientHeight
    if (!engine.loadedImg || pw <= 0 || ph <= 0) return
    const imgAR = engine.imgSize.w / engine.imgSize.h
    let cssW, cssH
    if (pw / ph > imgAR) { cssW = pw; cssH = pw / imgAR }
    else { cssH = ph; cssW = ph * imgAR }
    canvas.style.width = cssW + 'px'
    canvas.style.height = cssH + 'px'
  }

  // 5. State + image. setState before setImage so the engine's internal analyze()
  // uses the correct tail/mode (mirrors generate.js).
  // Kept around un-substituted: applyScroll re-derives the background bands from this
  // whenever the colour scheme flips, so it must stay the preset's authored values.
  const baseState = presetToState(settings)
  engine.setState(baseState)
  const img = new Image()
  img.src = src
  try {
    await img.decode()
    await engine.setImage(img)
  } catch {
    canvas.remove() // styler image failed → poster img stays as the fallback
    ctrl.setVisible = null
    reveal(el)
    return
  }

  // 6. No pointer tracking — mouse interactivity is disabled on this banner
  // (the cursor is parked off-canvas above), so the base motion runs alone.
  // A wrapper ResizeObserver re-fits when sync-height resolves from 0 (which
  // does not fire window 'resize').
  new ResizeObserver(() => {
    engine.fitCanvasToImage()
    if (!running) renderStatic() // keep the frozen/idle frame correct after a resize
  }).observe(wrapper)

  // 7. Show a static frame immediately (idle look == the baked PNG), then let the
  // idle-motion policy decide whether the loop runs.
  engine.fitCanvasToImage()
  started = true
  renderStatic()
  update()
}

if (document.body) {
  const nodes = document.querySelectorAll(SELECTOR)
  if (nodes.length) {
    // Reduced motion/data never upgrades to the live canvas — but the banner still has
    // to appear, so reveal the baked poster rather than leave it held at opacity 0.
    // (Reduced MOTION also hides the wrapper outright in CSS; reduced DATA does not.)
    if (prefersReducedMotion()) nodes.forEach(reveal)
    else {
      const io = new IntersectionObserver(onIntersect, { rootMargin: '200px' })
      nodes.forEach((n) => io.observe(n))
    }
  }
}
